/**
 * @sazeo/sdk — Surveys hooks
 *
 * TanStack Query wrappers for the surveys addon. All calls route through
 * the Edge Function at `/addons/surveys/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Survey {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  status: 'draft' | 'active' | 'closed';
  anonymous: boolean;
  closes_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SurveyQuestion {
  id: string;
  tenant_id: string;
  survey_id: string;
  question: string;
  kind: 'rating' | 'text' | 'yes_no' | 'multiple_choice';
  options: string[] | null;
  sort_order: number;
}

export interface SurveyCreateInput {
  title: string;
  description?: string | null;
  anonymous?: boolean;
  closes_at?: string | null;
}

export interface SurveyUpdateInput extends Partial<SurveyCreateInput> {
  status?: Survey['status'];
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const surveyKeys = {
  all: ['surveys'] as const,
  list: (tenantId: string) => [...surveyKeys.all, 'list', tenantId] as const,
  questions: (surveyId: string) => [...surveyKeys.all, 'questions', surveyId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useSurveys(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: surveyKeys.list(tenantId),
    queryFn: () => apiFetch<Survey[]>(supabase, 'addons/surveys/surveys'),
    enabled: Boolean(tenantId),
  });
}

export function useSurveyQuestions(
  supabase: SupabaseClientLike,
  tenantId: string,
  surveyId: string,
) {
  return useQuery({
    queryKey: surveyKeys.questions(surveyId),
    queryFn: () =>
      apiFetch<SurveyQuestion[]>(supabase, `addons/surveys/surveys/${surveyId}/questions`),
    enabled: Boolean(tenantId) && Boolean(surveyId),
  });
}

export function useCreateSurvey(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SurveyCreateInput) =>
      apiFetch<Survey>(supabase, 'addons/surveys/surveys', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: surveyKeys.list(tenantId) });
    },
  });
}

export function useUpdateSurvey(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: SurveyUpdateInput }) =>
      apiFetch<Survey>(supabase, `addons/surveys/surveys/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: surveyKeys.list(tenantId) });
    },
  });
}

export function useDeleteSurvey(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/surveys/surveys/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: surveyKeys.list(tenantId) });
    },
  });
}

export interface SurveyResponseItem {
  question_id: string;
  answer_text?: string;
  answer_num?: number;
}

export function useSubmitSurveyResponse(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, responses }: { surveyId: string; responses: SurveyResponseItem[] }) =>
      apiFetch<void>(supabase, `addons/surveys/surveys/${surveyId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ responses }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: surveyKeys.list(tenantId) });
    },
  });
}
